import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Live community channel — the OG `db.channel('cm:'+id)`. Subscribes to
 * postgres_changes on `community_messages` (INSERT/UPDATE/DELETE) and invalidates
 * the message cache so edits/sends/deletes from any client appear live. Also
 * carries a typing broadcast (`self:false`) so "X is typing…" shows without DB
 * writes.
 */
export function useCommunityRealtime(communityId: string, selfName: string | null) {
  const qc = useQueryClient();
  const [typing, setTyping] = useState<string[]>([]);
  const typingRef = useRef<Map<string, number>>(new Map());
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    if (!communityId) return;
    const channel = supabase.channel(`cm:${communityId}`, {
      config: { broadcast: { self: false } },
    });

    channel
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'community_messages', filter: `community_id=eq.${communityId}` },
        () => {
          void qc.invalidateQueries({ queryKey: ['cv', 'messages', communityId] });
        },
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'community_message_reactions' },
        () => {
          void qc.invalidateQueries({ queryKey: ['cv', 'reactions', communityId] });
        },
      )
      .on('broadcast', { event: 'typing' }, (msg) => {
        const name = (msg.payload as { name?: string })?.name;
        if (!name) return;
        typingRef.current.set(name, Date.now());
        setTyping([...typingRef.current.keys()]);
      })
      .subscribe();

    channelRef.current = channel;

    // Expire stale typing names.
    const interval = setInterval(() => {
      const now = Date.now();
      let changed = false;
      for (const [name, ts] of typingRef.current) {
        if (now - ts > 3500) {
          typingRef.current.delete(name);
          changed = true;
        }
      }
      if (changed) setTyping([...typingRef.current.keys()]);
    }, 1500);

    return () => {
      clearInterval(interval);
      void supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [communityId, qc]);

  const lastSent = useRef(0);
  function sendTyping() {
    if (!selfName || !channelRef.current) return;
    const now = Date.now();
    if (now - lastSent.current < 1500) return; // throttle
    lastSent.current = now;
    void channelRef.current.send({ type: 'broadcast', event: 'typing', payload: { name: selfName } });
  }

  return { typing, sendTyping };
}
